import React, { createRef, useState } from "react";
import PropTypes from "prop-types";

// components
import FormInput from "core/FormInput";
import Add from "core/ReusableIcons/Add";
import Close from "core/ReusableIcons/Close";
import Minus from "core/ReusableIcons/Minus";
import Button from "core/Button";
import SrOnly from "core/SrOnly";
import colors from "core/colors";
import Tooltip from "core/Tooltip";

// helpers
import { getCsrfCookieName } from "js/helpers/gazetteGetCsrfCookieName";
import { getCookie } from "js/helpers/gazetteGetCookie";
import {
    convertCentsToDollars,
    roundToHundredth
} from "frontend/components/helpers";

// data
import {
    CodeContainer,
    AddCodeText,
    MinusContainer,
    PromoCodeSubmitForm,
    SuccessContainer,
    PromotionAppliedText,
    SavingsText,
    ButtonWrapper,
    PromoContainer
} from "./styles";
import {
    PRO_MONTHLY_COST,
    PRO_YEARLY_COST,
    toolTipMsg
} from "subscriptions/data";

const PromoCode = ({
    promoCodeValue,
    setPromoCodeValue,
    subscriptionPrices,
    setSubscriptionPrices,
    numSeats // # of people on team
}) => {
    const [showInputForCode, setShowInputForCode] = useState(false);
    // Setting it at null to be neutral on load
    // if true/false it would default to error or success state
    const [isPromoValid, setIsPromoValid] = useState(null);
    const [promoApplied, setPromoApplied] = useState(false);
    const [discountAdded, setDiscountAdded] = useState({
        type: "",
        amount: 0
    });
    const formRef = createRef();
    const CENTS = "cents";
    const PERCENT = "percent";
    const monthlyNum = parseFloat(subscriptionPrices.monthly);
    const yearlyNum = parseFloat(subscriptionPrices.yearly);

    const sendRequest = async () => {
        const csrfCookieName = getCsrfCookieName();
        const csrftoken = getCookie({ name: csrfCookieName });

        const routeURL = `/coupon/${promoCodeValue}`;

        let response = await fetch(routeURL, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "X-CSRFToken": csrftoken
            }
        });

        // we use the server code to determine valid codes
        // so we can toggle error states in FormInput.
        // using true/ false causes too early validation issues
        if (!response.ok) {
            setIsPromoValid(404);
        } else {
            setIsPromoValid(200);
            response = await response.json();
            return response;
        }
    };

    const handlePromoCodeSubmit = async e => {
        e.preventDefault();
        const response = await sendRequest();

        // Promo codes can either be % off or $ off
        if (response.coupon.percent_off) {
            const percentOff = response.coupon.percent_off / 100;
            handlePercentOff(percentOff);
        } else if (response.coupon.amount_off) {
            // all dollar amounts come back in cents from the server
            const converted = convertCentsToDollars({
                cents: response.coupon.amount_off
            });

            // in the event user is on teams flow and has X number of members they
            // are paying for (numSeats) we want to multiply that number so they
            // get the promo code off per member they are paying for
            const amountOff = numSeats ? converted * numSeats : converted;
            handleAmountOff(amountOff);
        }
    };

    const handlePercentOff = percentOff => {
        const newMonthlyPrice = monthlyNum - monthlyNum * percentOff;
        const newYearlyPrice = yearlyNum - yearlyNum * percentOff;

        setSubscriptionPrices({
            yearly: roundToHundredth({ num: newYearlyPrice }),
            monthly: roundToHundredth({ num: newMonthlyPrice })
        });
        setPromoApplied(true);
        // used in case a user removes the promo code
        setDiscountAdded({
            type: PERCENT,
            amount: percentOff
        });
    };

    const handleAmountOff = amountOff => {
        const newYearlyPrice = yearlyNum - amountOff;
        const newMonthlyPrice = monthlyNum - amountOff;

        setSubscriptionPrices({
            yearly: roundToHundredth({ num: newYearlyPrice }),
            monthly: roundToHundredth({ num: newMonthlyPrice })
        });

        setPromoApplied(true);
        // used in case a user removes the promo code
        setDiscountAdded({
            type: CENTS,
            amount: amountOff
        });
    };

    const resetPromoCode = () => {
        setPromoApplied(false);
        setShowInputForCode(false);
        setIsPromoValid(null);

        setSubscriptionPrices({
            yearly: roundToHundredth({
                num: PRO_YEARLY_COST * parseInt(numSeats)
            }),
            monthly: roundToHundredth({
                num: PRO_MONTHLY_COST * parseInt(numSeats)
            })
        });
    };

    const AppliedCodeSuccess = () => (
        <SuccessContainer>
            <div>
                <PromotionAppliedText size="md" copyColor={colors.midnight}>
                    Applied promo
                </PromotionAppliedText>

                <SavingsText size="sm" copyColor={colors.ultramarine}>
                    {discountAdded.type === CENTS
                        ? `Saving $${roundToHundredth({
                              num: discountAdded.amount
                          })}`
                        : `Saving ${Math.ceil(discountAdded.amount * 100)}%`}
                </SavingsText>
            </div>
            <ButtonWrapper onClick={() => resetPromoCode()}>
                <Tooltip size="small" message={toolTipMsg}>
                    <Close size="sm" />
                </Tooltip>
            </ButtonWrapper>
        </SuccessContainer>
    );

    const InitialAddCode = () => (
        <PromoCodeSubmitForm onSubmit={handlePromoCodeSubmit} ref={formRef}>
            <MinusContainer
                onClick={() => setShowInputForCode(!showInputForCode)}
            >
                <Minus fillColor={colors.storm} />
                <SrOnly>Close Promo Code information form field</SrOnly>
            </MinusContainer>

            <FormInput
                placeholderText="Promo Code"
                labelText="Promo Code"
                size="md"
                value={promoCodeValue}
                onChange={setPromoCodeValue}
                isPromoValid={isPromoValid} // 200 for true, 404 for false, null for neutral
            />
            <Button size="md">Apply</Button>
        </PromoCodeSubmitForm>
    );

    const ShowInputField = () => (
        <ButtonWrapper
            onClick={() => setShowInputForCode(!showInputForCode)}
            showAdd
        >
            <PromoContainer>
                <Add size="sm" fillColor={colors.fossil} />
                <SrOnly>Open Promo Code information form field</SrOnly>
                <AddCodeText size="md">Add promo code</AddCodeText>
            </PromoContainer>
        </ButtonWrapper>
    );

    if (promoApplied) {
        return (
            <CodeContainer bottomSpacing={true}>
                {AppliedCodeSuccess()}
            </CodeContainer>
        );
    } else if (showInputForCode) {
        return <CodeContainer>{InitialAddCode()}</CodeContainer>;
    } else {
        return (
            <CodeContainer bottomSpacing={true}>
                {ShowInputField()}
            </CodeContainer>
        );
    }
};

PromoCode.propTypes = {
    promoCodeValue: PropTypes.string.isRequired,
    setPromoCodeValue: PropTypes.func.isRequired,
    subscriptionPrices: PropTypes.shape({
        yearly: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        monthly: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    }).isRequired,
    setSubscriptionPrices: PropTypes.func.isRequired,
    numSeats: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default PromoCode;
